import { useCallback, useMemo, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { getDatasetColor } from '../artifact-colors';
import { useIsExporting } from '../artifact-export-context';
import {
  buildPieSlices,
  formatDonutTotal,
  formatSlicePercent,
  sumPieTotal,
  truncateDonutCaption,
} from './freeform-chart-artifact.helpers';
import { CHART_HEIGHT, ChartTooltip, EmptyState, SeriesLegend } from './freeform-chart-shared';
import type { FreeformChartConfig, FreeformChartData } from './freeform-chart-artifact.type';

const RADIAN = Math.PI / 180;

/**
 * Renders the pie freeform chart type as a donut: `[label, value]` rows become
 * slices with whole-number percent labels, the summed total in the hole, and a
 * clickable legend toggling slice visibility.
 *
 * @param props.payload - The validated columnar `{columns, rows}` data.
 * @param props.config - Presentation config (the y label captions the total).
 * @returns The donut with its legend.
 */
function FreeformDonut({ payload, config }: { payload: FreeformChartData; config: FreeformChartConfig }) {
  const isExporting = useIsExporting();
  const slices = useMemo(() => buildPieSlices(payload.rows), [payload.rows]);

  // Slices are toggled by index, not label — duplicate labels are legal in
  // freeform data and must not share one legend checkbox.
  const [visibleSlices, setVisibleSlices] = useState<Record<string, boolean>>({});

  const toggleSlice = useCallback((index: string) => {
    setVisibleSlices((prev) => ({ ...prev, [index]: prev[index] === false }));
  }, []);

  const shownSlices = useMemo(
    () => slices.map((slice, idx) => ({ slice, idx })).filter(({ idx }) => visibleSlices[String(idx)] !== false),
    [slices, visibleSlices],
  );

  if (slices.length === 0) {
    return <EmptyState>No data to display</EmptyState>;
  }

  const total = sumPieTotal(shownSlices.map(({ slice }) => slice));
  const totalCaption = `Total ${config.yAxisLabel || payload.columns[1] || ''}`.trim();

  return (
    <>
      <div style={{ width: '100%', height: `${CHART_HEIGHT}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={(props) => <ChartTooltip {...props} />} />
            <Pie
              // Slices carry only label/value — recharts spreads data props
              // into label props, so a `percent` field here would shadow the
              // computed 0-1 `percent` the slice label renders from.
              data={shownSlices.map(({ slice }) => ({ label: slice.label, value: slice.value }))}
              dataKey="value"
              nameKey="label"
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={1}
              label={renderSliceLabel}
              labelLine={false}
              isAnimationActive={!isExporting}
            >
              {shownSlices.map(({ idx }) => (
                <Cell key={idx} fill={getDatasetColor(idx)} />
              ))}
            </Pie>
            <DonutCenterText total={total} caption={totalCaption} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <SeriesLegend
        items={slices.map((slice, idx) => ({ id: String(idx), name: slice.label, color: getDatasetColor(idx) }))}
        visible={visibleSlices}
        onToggle={toggleSlice}
      />
    </>
  );
}

function renderSliceLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
}) {
  if (cx == null || cy == null || midAngle == null || innerRadius == null || outerRadius == null || percent == null) {
    return null;
  }
  // Hide labels on slivers where the % text would overflow the slice.
  if (percent < 0.05) return null;
  const radius = innerRadius + (outerRadius - innerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#1a1a1a" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {formatSlicePercent(percent)}
    </text>
  );
}

function DonutCenterText({ total, caption }: { total: number; caption: string }) {
  // Rendered as a sibling of <Pie> inside the SVG; recharts passes no
  // geometry to plain children, so center on the 50%/50% viewport point.
  const display = formatDonutTotal(total);
  return (
    <g>
      <text x="50%" y="50%" textAnchor="middle">
        <tspan dy={caption ? -4 : 4} fontSize={22} fontWeight={600} fill="var(--ink-darkest)">
          {display}
        </tspan>
      </text>
      {caption && (
        <text x="50%" y="50%" textAnchor="middle">
          <title>{caption}</title>
          <tspan dy={16} fontSize={11} fill="var(--ink-lighter)">
            {truncateDonutCaption(caption)}
          </tspan>
        </text>
      )}
    </g>
  );
}

export { FreeformDonut };
